import { useState, useCallback } from "react";
import { toast } from 'sonner';
import { generateExperienceDescription } from "@/utils/experienceDescriptionGenerator";
import { ExperienceItem } from "@/hooks/useProfile";

interface GenerateDescriptionParams {
  position: string;
  company: string;
  startDate: string;
  endDate?: string;
  current?: boolean;
  skills?: string[];
}

export function useExperienceDescription(
  experienceItem: ExperienceItem,
  updateDescription: (description: string) => void,
  skills?: string[] | null
) {
  const [isGenerating, setIsGenerating] = useState(false);

  const validateInputs = useCallback((): string | null => {
    if (
      !experienceItem.position ||
      !experienceItem.company ||
      !experienceItem.start_date
    ) {
      return "Please fill in position, company, and start date to generate a description.";
    }
    return null;
  }, [experienceItem]);

  const handleGenerateDescription = useCallback(async () => {
    const error = validateInputs();
    if (error) {
      toast.error(error);
      return;
    }

    setIsGenerating(true);
    try {
      const params: GenerateDescriptionParams = {
        position: experienceItem.position,
        company: experienceItem.company,
        startDate: experienceItem.start_date,
        endDate: experienceItem.end_date,
        current: experienceItem.current,
        skills: skills || [],
      };

      const generatedDescription = await generateExperienceDescription(params);
      updateDescription(generatedDescription);

      toast.success("AI has generated a description for your experience.");
    } catch (error) {
      toast.error("There was a problem generating your description. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [experienceItem, skills, updateDescription, validateInputs]);

  return {
    isGenerating,
    handleGenerateDescription,
  };
}
